import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { ReactComponent as UserIcon } from 'assets/icon-user.svg';
import { ReactComponent as LockIcon } from 'assets/icon-lock.svg';

import { TextField, PasswordField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import { LabelError } from 'shared/components/Labels';
import MainSpinner from 'shared/components/MainSpinner';
import { ALERT_TYPE_ERROR } from 'shared/utils';
import { colors } from 'shared/defaultTheme';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { Information, InformationModal } from 'shared/components/SharedStyles/SharedStyles';
import InformationPersonal from 'shared/components/InformationPersonal';

import useApi from 'hooks/useApi';

import { showAlert } from 'redux/actions/alertActions';
import { setLogin } from 'redux/actions/authActions';
import { changeTheme, setClientLink } from 'redux/actions/themeActions';
import { changeDefaultLanguage } from 'redux/actions/topBarActions';

import { LineColor } from '../styles';
import SelectClient from './SelectClient';

const LogInForm = ({ secondaryColor }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { idApp } = useSelector((state) => state.auth);
  const { customAreaApi } = useApi();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [{ loading }, login] = customAreaApi(
    {
      url: `${window.API_V1}/login/get`,
      method: 'POST',
    },
    { manual: true }
  );
  const [recaptchaValue, setRecaptchaValue] = useState();
  const [openInformationModal, setOpenInformationModal] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [loginData, setLoginData] = useState();

  const doLogin = (data) => {
    const { config, relations } = data;
    dispatch(setLogin(data));
    i18n.changeLanguage(data.i18);
    dispatch(changeDefaultLanguage(data.i18));
    if (relations) {
      const existClientLink = relations.some((x) => x === 'clientlink');
      dispatch(setClientLink(existClientLink));
    }
    if (config != null && Object.keys(config).length > 0) {
      dispatch(changeTheme(config));
      history.push('/home');
      return;
    }
    history.push('/general_customization');
  };

  const onSubmit = async (values) => {
    try {
      if (values && recaptchaValue) {
        const { user, password } = values;
        const {
          data: { data, errors: loginErrors },
        } = await login({
          data: {
            idApp,
            login: user,
            password,
            domain: window.location.origin,
          },
        });
        if (loginErrors.length) {
          dispatch(
            showAlert({
              alertType: ALERT_TYPE_ERROR,
              alertMessage: t('login_invalid_credentials'),
            })
          );
          return;
        }

        if (!!data?.clients && Array.isArray(data.clients) && data.clients.length > 1) {
          setLoginData(data);
          setClientsList(data.clients);
        } else if (!!data?.clients && Array.isArray(data.clients) && data.clients.length === 1) {
          doLogin({
            ...data,
            clientId: data.clients[0].clientId,
          });
        } else {
          doLogin(data);
        }
      }
    } catch (ex) {
      if (ex.response) {
        if (ex.response?.status !== 400) {
          dispatch(
            showAlert({
              alertType: ALERT_TYPE_ERROR,
              alertMessage: t('general_error_message'),
            })
          );
          console.error('Error login: ', ex);
        } else {
          dispatch(
            showAlert({
              alertType: ALERT_TYPE_ERROR,
              alertMessage: t('login_invalid_credentials'),
            })
          );
        }
      }
    }
  };

  const onChangeRecaptcha = (value) => setRecaptchaValue(value);

  const onCloseInformationModal = () => {
    setOpenInformationModal(false);
  };

  if (clientsList.length > 0) {
    return <SelectClient loginData={loginData} doLogin={doLogin} />;
  }

  return (
    <form className="form mt-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="form__form-group form__form-group--user">
        <Controller
          name="user"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              placeholder={t('login_username')}
              Icon={() => (
                <UserIcon fill={errors.user !== undefined ? colors.error : secondaryColor} />
              )}
              borderRadius={2}
              value={field.value}
              onChange={field.onChange}
              error={errors.user !== undefined}
            />
          )}
        />
        {errors.user && (
          <LabelError className="account__label-error" label={t('login_required_user')} />
        )}
      </div>
      <div className="form__form-group form__form-group--password">
        <Controller
          name="password"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <PasswordField
              placeholder={t('login_password')}
              Icon={() => (
                <LockIcon fill={errors.password !== undefined ? colors.error : secondaryColor} />
              )}
              borderRadius={2}
              value={field.value}
              onChange={field.onChange}
              error={errors.password !== undefined}
            />
          )}
        />
        {errors.password && (
          <LabelError className="account__label-error" label={t('login_required_password')} />
        )}
      </div>
      <div className="account__recaptcha__wrapper">
        <ReCAPTCHA sitekey={window.REACT_APP_RECAPTCHA_SITE_KEY} onChange={onChangeRecaptcha} />
      </div>
      <div className="account__remember-me__wrapper">
        <Link className="form__label" to="/forgot_password">
          {i18n.t('login_forgot_password')}
        </Link>
      </div>
      <Button
        className="account_login-btn"
        type="submit"
        label={t('login_btn_session')}
        color="primary"
        full
        disabled={!recaptchaValue}
      />
      {loading && <MainSpinner />}
      <LineColor color={secondaryColor} height={1} borderWidth={1} marginTop={0} />
      <Information className="d-flex justify-content-center w-100 mt-3">
        <a href onClick={() => setOpenInformationModal(true)}>
          {t('login_information_data_personals')}
        </a>
      </Information>
      <InformationModal isOpen={openInformationModal} toggle={onCloseInformationModal} size="lg">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseInformationModal}
        />
        <ModalBody>
          <InformationPersonal onCancel={onCloseInformationModal} />
        </ModalBody>
      </InformationModal>
    </form>
  );
};

LogInForm.propTypes = {
  secondaryColor: PropTypes.string.isRequired,
};

export default LogInForm;
